import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Button } from '../components/ui/button';
import { 
  UserCheck, TrendingUp, TrendingDown, Clock, Target, 
  ChevronRight, ChevronDown, Info, Zap, Star,
  MessageSquare, Calendar, FileText, Shield, CheckCircle,
  AlertTriangle, BarChart3
} from 'lucide-react';
import AEDIcon from './icons/AEDIcon';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Agent Performance Score Component
 * 
 * Shows ARS (Agent Risk Score) with:
 * - Tier display (professional, precise)
 * - Category breakdown (expandable)
 * - Improvement guidance with clear levers
 * - Commission impact (neutral tone)
 * 
 * Agents are professionals - they require more precision.
 */
const AgentPerformanceScore = ({ token, agentId }) => {
  const [scoreData, setScoreData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    if (token && agentId) {
      fetchScore();
    } else {
      setLoading(false);
    }
  }, [token, agentId]);

  const fetchScore = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/agent/my-score`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        console.error('ARS API error:', res.status);
        setLoading(false);
        return;
      }
      const data = await res.json();
      if (data.success) {
        setScoreData(data.data);
      } else if (data.data) {
        setScoreData(data.data);
      }
    } catch (err) {
      console.error('Error fetching ARS:', err);
    }
    setLoading(false);
  };

  const getTierConfig = (tier) => {
    const configs = {
      'TIER_A': {
        label: 'Tier A – Elite Performer',
        shortLabel: 'Tier A',
        color: 'text-emerald-400',
        bgColor: 'bg-emerald-600/10 border-emerald-600/30',
        gradientClass: 'from-emerald-600/20 to-emerald-600/5',
        description: 'Top reliability tier - priority deal flow, full commission',
        commissionAdjustment: '+0%',
        commissionNote: 'Full commission rate'
      },
      'TIER_B': {
        label: 'Tier B – Stable Operator',
        shortLabel: 'Tier B',
        color: 'text-blue-400',
        bgColor: 'bg-blue-600/10 border-blue-600/30',
        gradientClass: 'from-blue-600/20 to-blue-600/5',
        description: 'Strong performance - standard deal flow',
        commissionAdjustment: '-5%',
        commissionNote: 'Minor adjustment applied'
      },
      'TIER_C': {
        label: 'Tier C – Improvement Needed',
        shortLabel: 'Tier C',
        color: 'text-amber-400',
        bgColor: 'bg-amber-600/10 border-amber-600/30',
        gradientClass: 'from-amber-600/20 to-amber-600/5',
        description: 'Performance requires attention - limited priority',
        commissionAdjustment: '-10%',
        commissionNote: 'Adjustment applied - improve to restore'
      },
      'RESTRICTED': {
        label: 'Restricted – Under Review',
        shortLabel: 'Restricted',
        color: 'text-red-400',
        bgColor: 'bg-red-600/10 border-red-600/30',
        gradientClass: 'from-red-600/20 to-red-600/5',
        description: 'Significant improvements required',
        commissionAdjustment: '-20%',
        commissionNote: 'Contact support for guidance'
      },
      'UNSCORED': {
        label: 'New Agent',
        shortLabel: 'Unscored',
        color: 'text-gray-400',
        bgColor: 'bg-gray-600/10 border-gray-600/30',
        gradientClass: 'from-gray-600/20 to-gray-600/5',
        description: 'Complete transactions to establish your score',
        commissionAdjustment: '0%',
        commissionNote: 'Standard rate until scored'
      }
    };
    return configs[tier] || configs['UNSCORED'];
  };

  // Category scores breakdown
  const getCategoryScores = () => {
    if (!scoreData) return [];
    
    const categories = [
      {
        id: 'responsiveness',
        name: 'Responsiveness',
        score: scoreData.responsiveness_score || scoreData.responseTimeScore || 75,
        icon: Clock,
        description: 'Speed of response to viewing requests and offers',
        metrics: [
          { label: 'Avg response time', value: scoreData.avgResponseTime ? `${scoreData.avgResponseTime}h` : '< 4h target' },
          { label: 'SLA compliance', value: scoreData.slaCompliance ? `${scoreData.slaCompliance}%` : '95%+' }
        ],
        improvement: 'Reduce response time below 4 hours'
      },
      {
        id: 'execution',
        name: 'Execution Discipline',
        score: scoreData.execution_score || scoreData.timelineDisciplineScore || 70,
        icon: Target,
        description: 'Meeting deadlines and managing timelines',
        metrics: [
          { label: 'Deadlines met', value: scoreData.deadlinesMet || '90%+' },
          { label: 'Extensions requested', value: scoreData.extensionCount || '0' }
        ],
        improvement: 'Maintain 0 missed SLAs for 21 days'
      },
      {
        id: 'integrity',
        name: 'Offer Integrity',
        score: scoreData.integrity_score || scoreData.offerIntegrityScore || 85,
        icon: Shield,
        description: 'Handling offers professionally and transparently',
        metrics: [
          { label: 'Offers handled', value: scoreData.offersHandled || '0' },
          { label: 'Disputes', value: scoreData.disputeCount || '0' }
        ],
        improvement: 'Maintain clean offer handling record'
      },
      {
        id: 'completion',
        name: 'Completion Efficiency',
        score: scoreData.completion_score || scoreData.completionScore || 72,
        icon: CheckCircle,
        description: 'Closing deals efficiently relative to market',
        metrics: [
          { label: 'Time to close', value: scoreData.avgTimeToClose ? `${scoreData.avgTimeToClose} days` : 'Market avg' },
          { label: 'Close rate', value: scoreData.closeRate ? `${scoreData.closeRate}%` : '—' }
        ],
        improvement: 'Complete 2 consecutive deals without amendment'
      },
      {
        id: 'governance',
        name: 'Governance Events',
        score: scoreData.governance_score || scoreData.governanceScore || 80,
        icon: FileText,
        description: 'Compliance and amendment frequency',
        metrics: [
          { label: 'Amendments initiated', value: scoreData.amendmentCount || '0' },
          { label: 'Conditions', value: scoreData.conditionCount || '0' }
        ],
        improvement: 'Minimize post-signing amendments'
      }
    ];
    
    return categories;
  };

  // Get improvement guidance
  const getImprovementGuidance = () => {
    if (!scoreData) return [];
    const guidance = [];
    
    const tier = scoreData.tier || 'UNSCORED';
    const categories = getCategoryScores();
    
    // Find lowest scoring categories
    const sorted = [...categories].sort((a, b) => a.score - b.score);
    const lowestTwo = sorted.slice(0, 2);
    
    lowestTwo.forEach(cat => {
      if (cat.score < 80) {
        guidance.push({
          category: cat.name,
          action: cat.improvement,
          currentScore: cat.score
        });
      }
    });
    
    // Add tier-specific guidance
    if (tier === 'TIER_B') {
      guidance.push({
        category: 'Tier Upgrade',
        action: 'Maintain Tier A performance for 30 days to restore full commission',
        currentScore: null
      });
    } else if (tier === 'TIER_C' || tier === 'RESTRICTED') {
      guidance.push({
        category: 'Priority',
        action: 'Focus on responsiveness and deadline compliance first',
        currentScore: null
      });
    }
    
    return guidance;
  };

  const getScoreColor = (score) => {
    if (score >= 85) return 'text-emerald-400';
    if (score >= 70) return 'text-blue-400';
    if (score >= 55) return 'text-amber-400';
    return 'text-red-400';
  };

  if (loading) {
    return (
      <Card className="glass-card border-white/10">
        <CardContent className="p-6 text-center">
          <UserCheck className="w-8 h-8 animate-pulse mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading performance score...</p>
        </CardContent>
      </Card>
    );
  }

  if (!scoreData) {
    return null;
  }

  // Handle INSUFFICIENT_DATA status
  if (scoreData.status === 'INSUFFICIENT_DATA') {
    return (
      <Card className="glass-card border border-white/10" data-testid="agent-performance-score">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <UserCheck className="w-5 h-5 text-primary" />
            Performance Reliability Score
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Your reliability score influences deal flow priority and commission multipliers
          </p>
        </CardHeader>
        <CardContent>
          <div className="p-4 rounded-lg bg-primary/10 border border-primary/20 text-center">
            <Star className="w-8 h-8 mx-auto text-primary mb-3" />
            <h3 className="text-lg font-display text-primary">Building Your Score</h3>
            <p className="text-sm text-muted-foreground mt-2">
              {scoreData.message || 'Complete more transactions to build your score'}
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              Required: {scoreData.min_required_deals || 3} completed deals
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const tier = scoreData.tier || 'UNSCORED';
  const tierConfig = getTierConfig(tier);
  const score = scoreData.score || 50;
  const categories = getCategoryScores();
  const guidance = getImprovementGuidance();

  return (
    <Card className={`glass-card border ${tierConfig.bgColor}`} data-testid="agent-performance-score">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <UserCheck className={`w-5 h-5 ${tierConfig.color}`} />
              Performance Reliability Score
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Your reliability score influences deal flow priority and commission multipliers
            </p>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setShowDetails(!showDetails)}
            className="text-xs"
          >
            {showDetails ? 'Less' : 'Breakdown'}
            <ChevronRight className={`w-4 h-4 ml-1 transition-transform ${showDetails ? 'rotate-90' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Tier Display */}
        <div className={`p-4 rounded-lg bg-gradient-to-r ${tierConfig.gradientClass}`}>
          <div className="flex items-center justify-between">
            <div>
              <h3 className={`text-xl font-display ${tierConfig.color}`}>
                {tierConfig.label}
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Score: {score} / 100
              </p>
            </div>
            <div className={`text-3xl font-display ${tierConfig.color}`}>
              {score}
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-3">
            <Progress 
              value={score} 
              className="h-2 bg-black/20"
            />
          </div>
          
          <p className="text-xs text-muted-foreground mt-2">
            {tierConfig.description}
          </p>
        </div>

        {/* Category Breakdown - Always Visible */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-primary" />
            Category Breakdown
          </h4>
          <div className="grid grid-cols-5 gap-2">
            {categories.map(cat => (
              <div 
                key={cat.id}
                className={`p-2 rounded cursor-pointer transition-all hover:bg-secondary/20 ${
                  expandedCategory === cat.id ? 'bg-secondary/20 ring-1 ring-primary/30' : 'bg-secondary/10'
                }`}
                onClick={() => setExpandedCategory(expandedCategory === cat.id ? null : cat.id)}
              >
                <div className="text-center">
                  <cat.icon className={`w-4 h-4 mx-auto ${getScoreColor(cat.score)}`} />
                  <p className={`text-lg font-display ${getScoreColor(cat.score)}`}>{cat.score}</p>
                  <p className="text-[10px] text-muted-foreground truncate">{cat.name}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Expanded Category Detail */}
        {expandedCategory && (
          <div className="p-3 rounded bg-secondary/10 border border-white/5">
            {(() => {
              const cat = categories.find(c => c.id === expandedCategory);
              if (!cat) return null;
              return (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h5 className="text-sm font-medium flex items-center gap-2">
                      <cat.icon className={`w-4 h-4 ${getScoreColor(cat.score)}`} />
                      {cat.name}
                    </h5>
                    <span className={`text-lg font-display ${getScoreColor(cat.score)}`}>
                      {cat.score}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">{cat.description}</p>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {cat.metrics.map((m, idx) => (
                      <div key={idx} className="text-xs">
                        <span className="text-muted-foreground">{m.label}: </span>
                        <span className="text-foreground">{m.value}</span>
                      </div>
                    ))}
                  </div>
                  <div className="pt-2 border-t border-white/10 mt-2">
                    <p className="text-xs text-amber-400 flex items-center gap-1">
                      <Zap className="w-3 h-3" />
                      To improve: {cat.improvement}
                    </p>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Improvement Guidance - Expanded View */}
        {showDetails && guidance.length > 0 && (
          <div className="space-y-2 pt-2 border-t border-white/10">
            <h4 className="text-sm font-medium text-amber-400 flex items-center gap-2">
              <Target className="w-4 h-4" />
              To Increase Your Tier
            </h4>
            <div className="space-y-2">
              {guidance.map((g, idx) => (
                <div key={idx} className="p-2 rounded bg-secondary/10 flex items-start gap-2">
                  <ChevronRight className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-muted-foreground">{g.action}</p>
                    {g.currentScore && (
                      <p className="text-xs text-muted-foreground/70 mt-0.5">
                        {g.category}: {g.currentScore}/100
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Commission Impact - Neutral Tone */}
        {showDetails && tier !== 'TIER_A' && (
          <div className="p-3 rounded bg-secondary/10 border border-white/5">
            <h5 className="text-xs font-medium text-foreground flex items-center gap-2">
              <AEDIcon className="w-3.5 h-3.5" />
              Reliability Adjustment Applied
            </h5>
            <p className="text-xs text-muted-foreground mt-1">
              Your current tier results in a{' '}
              <span className={tierConfig.color}>{tierConfig.commissionAdjustment}</span> adjustment.
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {tierConfig.commissionNote}
            </p>
            {tier === 'TIER_B' && (
              <p className="text-xs text-amber-400/80 mt-2">
                Maintain Tier A for 30 days to restore +0% base.
              </p>
            )}
          </div>
        )}

        {/* Mode Indicator */}
        <div className="text-center pt-2 border-t border-white/10">
          <Badge variant="outline" className="text-xs border-primary/30 text-primary/70">
            Shadow Mode - Score visible, adjustments not yet active
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
};

export default AgentPerformanceScore;
